import React, { useState } from 'react';
import { db } from '../firebase/config';
import { collection, addDoc, query, where, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore';

// Add these common disposable email domains to block
const BLOCKED_DOMAINS = [
  'startmail.com',
  'tempmail.com',
  'temp-mail.org',
  'guerrillamail.com',
  'disposablemail.com',
  'mailinator.com',
  'throwawaymail.com',
  // Suspicious pattern: if it contains your app name
  'proverbsdaily',
  // Add more as needed
];

const EmailSubscription = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    if (!email || !email.includes('@')) {
      setStatus('error');
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    // Add domain check
    const domain = email.toLowerCase().split('@')[1];
    if (BLOCKED_DOMAINS.some(blocked => domain.includes(blocked))) {
      setStatus('error');
      setErrorMessage('This email domain is not allowed. Please use a valid email address.');
      return;
    }

    try {
      // Check if email already exists
      const q = query(
        collection(db, 'subscribers'), 
        where('email', '==', email.toLowerCase())
      );
      
      console.log('Checking email:', email.toLowerCase()); // Debug log
      
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        // Instead of showing error, update the existing subscription
        const docRef = querySnapshot.docs[0].ref;
        await updateDoc(docRef, {
          status: 'active',
          subscriptionDate: serverTimestamp(),
          source: 'website_subscription',
          userAgent: window.navigator.userAgent,
          subscriptionAttemptTime: new Date().toISOString()
        });
        
        console.log('Successfully updated subscriber');
        setStatus('success');
        setEmail('');
        setShowSuccess(true);
        return;
      }

      console.log('Adding new subscriber...'); // Debug log

      // Add new subscriber with minimal fields
      const docRef = await addDoc(collection(db, 'subscribers'), {
        email: email.toLowerCase(),
        status: 'active',
        source: 'website_subscription',
        subscriptionDate: serverTimestamp()
      });

      console.log('Successfully added subscriber with ID:', docRef.id);
      setStatus('success');
      setEmail('');
      setShowSuccess(true);
      
      setTimeout(() => {
        setShowSuccess(false);
        setStatus('idle');
      }, 5000);
      
    } catch (error) {
      console.error('Detailed Subscription Error:', {
        code: error.code,
        message: error.message,
        fullError: error
      });
      
      // Log error to Firestore with more details
      try {
        await addDoc(collection(db, 'subscription_errors'), {
          email: email,
          errorCode: error.code,
          errorMessage: error.message,
          errorDetails: JSON.stringify(error, Object.getOwnPropertyNames(error)),
          timestamp: serverTimestamp(),
          userAgent: window.navigator.userAgent
        });
      } catch (logError) {
        console.error('Failed to log error:', logError);
      }

      // More specific error messages for users
      if (error.code === 'permission-denied') {
        setErrorMessage(`Subscription error: ${error.message}`);
      } else if (error.code === 'unavailable') {
        setErrorMessage('Service is temporarily offline. Please try again in a moment.');
      } else {
        setErrorMessage(`Subscription error: ${error.message}`);
      }
      setStatus('error');
    }
  };

  return (
    <div id="email-subscription" className="bg-gradient-to-br from-blue-900 to-purple-900 rounded-lg shadow-lg p-6 my-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Success Message Overlay */}
          {showSuccess && (
            <div className="absolute inset-0 bg-[#593174] bg-opacity-95 flex items-center justify-center p-6 transition-opacity duration-500 z-10">
              <div className="text-center">
                <div className="text-green-300 text-4xl mb-3">✓</div>
                <h3 className="text-white text-lg font-bold mb-2">Welcome to Proverbs Daily!</h3>
                <p className="text-purple-100 mb-3">
                  Thank you for subscribing. Get ready for daily wisdom and inspiration!
                </p>
              </div>
            </div>
          )}

          {/* Image Section */}
          <div className="w-full md:w-2/5">
            <div className="relative w-full h-[250px] md:h-[200px]">
              <img
                src="/email-prompt.jpg"
                alt="Proverbs Daily App"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Content Section */}
          <div className="w-full md:w-3/5 p-4 md:p-6">
            <h2 className="text-xl font-semibold text-white mb-2 text-center md:text-left">
              Start each day with wisdom that works.
            </h2>
            <p className="text-sm text-white mb-3 text-center md:text-left">
              Experience life-changing insights in just 2-5 minutes - connect ancient truth to your modern journey!
            </p>
            
            {status !== 'success' && (
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-1.5">
                <div className="flex-1">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="EMAIL ADDRESS*"
                    className="w-full px-3 py-2 rounded bg-white text-gray-900 text-sm"
                    required
                    disabled={status === 'loading'}
                  />
                </div>
                <button 
                  type="submit"
                  className="bg-violet-600 hover:bg-violet-700 text-white font-medium py-2 px-6 rounded-md text-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={status === 'loading'}
                >
                  {status === 'loading' ? 'SUBMITTING...' : 'SUBMIT'}
                </button>
              </form>
            )}
            
            {status === 'error' && (
              <p className="text-red-400 mt-2 text-xs">
                {errorMessage}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailSubscription;
